import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, MapPin, Building2, Route, MoreHorizontal } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { VisitorAlertsProvider, UnreadBadge, useVisitorAlerts } from "@/components/VisitorAlerts";

const TABS = [
  { to: "/today", label: "Today", icon: LayoutDashboard, id: "today" },
  { to: "/map", label: "Map", icon: MapPin, id: "map" },
  { to: "/leads", label: "Leads", icon: Building2, id: "leads" },
  { to: "/trips", label: "Trips", icon: Route, id: "trips" },
  { to: "/more", label: "More", icon: MoreHorizontal, id: "more" },
];

function BottomNav() {
  const { unread } = useVisitorAlerts();
  return (
    <nav
      data-testid="bottom-nav"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur md:hidden pb-safe"
    >
      <div className="mx-auto flex max-w-lg items-stretch justify-around">
        {TABS.map((t) => (
          <NavLink
            key={t.id}
            to={t.to}
            data-testid={`nav-tab-${t.id}`}
            className={({ isActive }) =>
              `flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-[11px] font-medium transition-colors ${
                isActive ? "text-teal-700" : "text-slate-400 hover:text-slate-600"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className="relative">
                  <t.icon className={`h-5 w-5 ${isActive ? "stroke-[2.5]" : ""}`} />
                  {t.id === "more" && unread > 0 && (
                    <span data-testid="nav-more-unread-badge" className="absolute -right-2 -top-1.5 rounded-full bg-rose-600 px-1 text-[9px] font-bold leading-4 text-white">{unread > 99 ? "99+" : unread}</span>
                  )}
                </span>
                <span>{t.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function SideRail() {
  const { user } = useAuth();
  const { unread } = useVisitorAlerts();
  return (
    <aside className="hidden md:flex md:w-60 md:flex-col md:border-r md:border-slate-200 md:bg-white">
      <div className="flex h-16 items-center justify-between border-b border-slate-200 px-5">
        <Logo />
        <NotificationBell testid="rail-notification-bell" />
      </div>
      <nav className="flex-1 space-y-1 p-3">
        {TABS.map((t) => (
          <NavLink
            key={t.id}
            to={t.to}
            data-testid={`rail-tab-${t.id}`}
            className={({ isActive }) =>
              `flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-teal-50 text-teal-800"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`
            }
          >
            <t.icon className="h-4.5 w-4.5" />
            {t.label}
            {t.id === "more" && <UnreadBadge count={unread} testid="rail-more-unread-badge" />}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-200 p-4">
        <p className="truncate text-sm font-semibold text-slate-800">{user?.name}</p>
        <p className="truncate text-xs capitalize text-slate-400">
          {user?.role?.replace(/_/g, " ")}
        </p>
      </div>
    </aside>
  );
}

export function AppShell({ children }) {
  const { user } = useAuth();
  return <VisitorAlertsProvider role={user?.role}><Shell>{children}</Shell></VisitorAlertsProvider>;
}

function Shell({ children }) {
  const location = useLocation();
  const active = TABS.find((t) => location.pathname.startsWith(t.to));
  return (
    <div className="flex min-h-screen bg-slate-50">
      <SideRail />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white/95 px-4 backdrop-blur md:hidden">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="font-heading text-sm font-bold text-slate-500">
              {active?.label}
            </span>
            <NotificationBell testid="mobile-notification-bell" />
          </div>
        </header>
        <main className="flex-1 pb-24 md:pb-8">{children}</main>
        <BottomNav />
      </div>
    </div>
  );
}
